import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios'; // Importar axios para chamadas externas
import api from '../services/api'; // Instância configurada de axios
import './AddressForm.css';
import TopBar from '../components/TopBar';
import PageTransition from '../components/ui/PageTransition';
import WizardStepper from '../components/ui/WizardStepper';
import Button from '../components/ui/Button';
import Spinner from '../components/ui/Spinner';
import Alert from '../components/ui/Alert';
import { AnimatePresence } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { AxiosError } from 'axios';

type AddressData = {
  resides_outside_the_country: string;
  cep: string;
  type_of_street: string;
  address: string;
  complement: string;
  neighborhood: string;
  number: string;
  UF: string;
  city_of_residence: string;
};

// Tipagem para a resposta de erro da API
interface ApiErrorResponse {
  message?: string;
}

const AddressForm: React.FC = () => {
  const [formData, setFormData] = useState<AddressData>({
    resides_outside_the_country: '',
    cep: '',
    type_of_street: '',
    address: '',
    complement: '',
    neighborhood: '',
    number: '',
    UF: '',
    city_of_residence: '',
  });

  const [addressId, setAddressId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [cepLoading, setCepLoading] = useState(false);
  const [autoFilled, setAutoFilled] = useState(false);
  const navigate = useNavigate();
  const { id_user, token, logout } = useAuth();

  useEffect(() => {
    if (!token || !id_user) {
      alert('Sessão expirada. Faça login novamente.');
      navigate('/rj');
      return;
    }

    const fetchAddress = async () => {
      try {
        const response = await api.get(`/address/${id_user}`);
        if (response.data.message === 'Address retrieved') {
          const { id_address, id_user: _, resides_outside_the_country, ...addressData } = response.data.data;
          setFormData({
            ...addressData,
            resides_outside_the_country: resides_outside_the_country === 1 ? 'sim' : 'não',
          });
          setAddressId(id_address);
        }
      } catch (err) {
        const error = err as AxiosError<ApiErrorResponse>;
        if (error.response?.status === 404) {
          console.log('Nenhum endereço encontrado para o usuário.');
        } else {
          setError(error.response?.data?.message || 'Erro ao carregar o endereço. Tente novamente.');
        }
      }
    };

    fetchAddress();
  }, [id_user, token, navigate]);

  const handleChange = async (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (name === 'cep') {
      const rawCep = value.replace(/\D/g, '');
      if (rawCep.length === 8) {
        setCepLoading(true);
        try {
          const res = await axios.get(`https://viacep.com.br/ws/${rawCep}/json/`);
          const data = res.data;

          if (!data.erro) {
            setFormData((prev) => ({
              ...prev,
              address: data.logradouro || '',
              neighborhood: data.bairro || '',
              city_of_residence: data.localidade || '',
              UF: data.uf || '',
            }));
            setAutoFilled(true);
            setTimeout(() => setAutoFilled(false), 1000);
          } else {
            setError('CEP não encontrado.');
          }
        } catch {
          setError('Erro ao buscar o CEP.');
        } finally {
          setCepLoading(false);
        }
      }
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);

    // Validação básica
    if (!formData.resides_outside_the_country) {
      setError('Selecione se reside fora do país.');
      setIsLoading(false);
      return;
    }

    if (formData.resides_outside_the_country === 'não') {
      const requiredFields: (keyof AddressData)[] = [
        'cep',
        'type_of_street',
        'address',
        'number',
        'neighborhood',
        'UF',
        'city_of_residence',
      ];
      for (const field of requiredFields) {
        if (!formData[field]) {
          setError(`O campo "${field}" é obrigatório.`);
          setIsLoading(false);
          return;
        }
      }
    }

    try {
      const payload = {
        ...formData,
        resides_outside_the_country: formData.resides_outside_the_country === 'sim',
      };

      const url = addressId ? `/address/${id_user}` : '/address';
      const method = addressId ? 'put' : 'post';

      await api({ method, url, data: payload });
      navigate('/rj/dependents');
    } catch (err) {
      const error = err as AxiosError<ApiErrorResponse>;
      const message = error.response?.data?.message || 'Erro ao salvar o endereço.';
      setError(message);
      setIsLoading(false);
    }
  };

  const showAddressFields = formData.resides_outside_the_country.toLowerCase() === 'não';

  const autoFillableFields = ['address', 'neighborhood', 'city_of_residence', 'UF'];

  return (
    <>
      <TopBar />
      <PageTransition>
        <div className="address-wrapper">
          <WizardStepper current={2} />
          <div className="address-form-container">
            <form onSubmit={handleSubmit} className="address-form">
              <h2>Formulário de Endereço</h2>
              <AnimatePresence>
                {error && <Alert variant="error">{error}</Alert>}
              </AnimatePresence>

              <div className="form-group">
                <label htmlFor="resides_outside_the_country">Reside fora do país:</label>
                <select
                  id="resides_outside_the_country"
                  name="resides_outside_the_country"
                  value={formData.resides_outside_the_country}
                  onChange={handleChange}
                  className="form-input"
                  disabled={isLoading}
                >
                  <option value="">Selecione</option>
                  <option value="sim">Sim</option>
                  <option value="não">Não</option>
                </select>
              </div>

              {showAddressFields && (
                <>
                  {[
                    { key: 'cep', label: 'CEP', placeholder: 'Digite o CEP' },
                    { key: 'type_of_street', label: 'Tipo de logradouro', placeholder: '' },
                    { key: 'address', label: 'Endereço', placeholder: '' },
                    { key: 'complement', label: 'Complemento', placeholder: '' },
                    { key: 'neighborhood', label: 'Bairro', placeholder: '' },
                    { key: 'number', label: 'Número', placeholder: '' },
                    { key: 'UF', label: 'UF', placeholder: '' },
                    { key: 'city_of_residence', label: 'Cidade de residência', placeholder: '' },
                  ].map(({ key, label, placeholder }) => (
                    <div
                      key={key}
                      className={`form-group ${autoFilled && autoFillableFields.includes(key) ? 'form-group--flash' : ''}`}
                    >
                      <label htmlFor={key}>
                        {label}:
                        {key === 'cep' && cepLoading && <Spinner className="cep-spinner" />}
                      </label>
                      <input
                        id={key}
                        name={key}
                        value={formData[key as keyof AddressData]}
                        onChange={handleChange}
                        className="form-input"
                        placeholder={placeholder}
                        disabled={isLoading}
                      />
                    </div>
                  ))}
                </>
              )}

              <div className="button-group">
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => navigate('/rj/personal-data')}
                  disabled={isLoading}
                >
                  Anterior
                </Button>
                <Button type="submit" loading={isLoading} disabled={isLoading}>
                  {isLoading ? 'Salvando…' : 'Próximo'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      </PageTransition>
    </>
  );
};

export default AddressForm;